import React from "react";
import { LogoMarkOnce, Portal } from "./registry";
/* ============================================================
   Fence Works — Auth gate (Supabase email + password)
   Renders a login / signup screen until there's a session.
   An account is required — there is no local / guest mode.
   ============================================================ */

function useAuth() {
  const enabled = !!(window.VFP_DB && window.VFP_DB.auth && window.VFP_DB.auth.enabled);
  const [user, setUser] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<any>(enabled);

  React.useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    let off = () => {};
    window.VFP_DB.auth.getUser().then((u) => { setUser(u); setLoading(false); });
    off = window.VFP_DB.auth.onChange((u) => setUser(u));
    return () => off && off();
  }, [enabled]);

  const api = {
    enabled, user, loading,
    signIn: (e, p) => window.VFP_DB.auth.signIn(e, p),
    signUp: (e, p, n, c, attr, opts) => window.VFP_DB.auth.signUp(e, p, n, c, attr, opts),
    resetPassword: (e) => window.VFP_DB.auth.resetPassword(e),
    updatePassword: (p) => window.VFP_DB.auth.updatePassword(p),
    signOut: async () => {
      try { await window.VFP_DB.auth.signOut(); } catch (e) {}
      // drop this device's cached store + identity so the next account starts clean
      try { localStorage.removeItem("vfp_state_v2"); localStorage.removeItem("fw_last_uid"); } catch (e) {}
      setUser(null);
    },
  };
  return api;
}

/* The "how did you hear about us" options. MIRRORS FW.HEARD_ABOUT in
   site/forms.js — the two signup paths must store the same closed set of keys
   or the reporting view can't group them ("google" vs "Google" vs "googled"). */
const HEARD_ABOUT: [string, string][] = [
  ["referral", "Another contractor told me"],
  ["email", "Email"],
  ["facebook_group", "Facebook Group"],
  ["google", "Google Search"],
  ["youtube", "YouTube"],
  ["mail", "Postcard / mail"],
  ["other", "Something else"],
];
// answers where the label alone tells you nothing — ask the follow-up
const HEARD_DETAIL = new Set(["referral", "facebook_group", "other"]);

/* Signup attribution stashed by app.tsx from the marketing-site handoff:
   the self-reported "how did you hear about us" plus the first-touch
   UTM/referrer captured on the visitor's first page view. Passed straight into
   auth.signUp so it lands in user metadata and the DB trigger writes it onto
   the new workspace. Returns null when someone signed up directly — an empty
   object would overwrite nothing but reads as "we asked and they said nothing",
   which is a different fact from "we never asked". */
function signupAttribution(askedHeard?: string, askedDetail?: string) {
  try {
    // the marketing site's answer wins — it was captured closer to the click
    const heard = localStorage.getItem("fw_prefill_heard") || askedHeard || "";
    const detail = localStorage.getItem("fw_prefill_heard_detail") || askedDetail || "";
    let attribution = null;
    try { attribution = JSON.parse(localStorage.getItem("fw_prefill_attribution") || "null"); } catch (e) {}
    if (!heard && !attribution) return null;
    return { heard_about: heard || null, heard_detail: detail || null, attribution: attribution || {} };
  } catch (e) { return askedHeard ? { heard_about: askedHeard, heard_detail: askedDetail || null, attribution: {} } : null; }
}

function AuthScreen({ auth }: any) {
  // prefill + default to signup when arriving from the marketing plan picker
  const pre = (k) => { try { return localStorage.getItem("fw_prefill_" + k) || ""; } catch (e) { return ""; } };
  const pendingPlan = (() => { try { return localStorage.getItem("fw_pending_plan"); } catch (e) { return null; } })();
  const [mode, setMode] = React.useState<any>(pendingPlan ? "signup" : "signin");   // signin | signup
  const [email, setEmail] = React.useState<any>(pre("email"));
  const [pw, setPw] = React.useState<any>("");
  const [name, setName] = React.useState<any>(pre("name"));
  const [company, setCompany] = React.useState<any>(pre("company"));
  // the marketing site may already have asked — don't ask twice
  const heardPrefilled = !!pre("heard");
  const [heard, setHeard] = React.useState<any>("");
  const [heardDetail, setHeardDetail] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [note, setNote] = React.useState<any>("");

  // team-invite landing (?invite=<token>, stashed pre-render): show WHO invited
  // them, lock the email to the invite, and default to creating the account.
  const inviteToken = (() => { try { return localStorage.getItem("fw_pending_invite") || ""; } catch (e) { return ""; } })();
  const [invite, setInvite] = React.useState<any>(null);   // null | {email,role,business} | "bad"
  React.useEffect(() => {
    const DB: any = (window as any).VFP_DB;
    if (!inviteToken || !DB || !DB.inviteInfo) return;
    DB.inviteInfo(inviteToken).then((info: any) => {
      if (info && info.email) { setInvite(info); setEmail(info.email); setMode("signup"); }
      else setInvite("bad");
    });
  }, [inviteToken]);
  const invited = invite && invite !== "bad";
  // arriving from "Make this my website" on a Lead Engine preview: the site is
  // waiting — App claims it into this account right after sign-in / signup
  const claiming = !invited && (() => { try { return !!localStorage.getItem("fw_pending_claim"); } catch (e) { return false; } })();
  // arriving from the marketing signup (steps 1–3 done on the site) → this is
  // step 4 of 4: name/company/email carried over, only a password to set
  const fromSite = mode === "signup" && !!pendingPlan && !invited && !claiming;
  const planName = (() => { try { const P = (window as any).PLAN; return pendingPlan && P ? P(pendingPlan).name : pendingPlan; } catch (e) { return pendingPlan; } })();

  async function submit(e) {
    e.preventDefault();
    setErr(""); setNote(""); setBusy(true);
    try {
      if (mode === "forgot") {
        await auth.resetPassword(email);
        setNote("Reset link sent — check your email (and spam). The link signs you in to set a new password.");
        return;
      }
      if (mode === "signup") {
        // A teammate's signup still gets its own workspace from the DB trigger
        // until the invite moves them into the team's. It must not carry the
        // team's name (FW Admin listed it as a brand-new customer under it),
        // so it gets none, and is flagged as an invite signup.
        const r = invited
          ? await auth.signUp(email, pw, name, "", signupAttribution(heard, heardDetail), { teamInvite: true })
          : await auth.signUp(email, pw, name, company, signupAttribution(heard, heardDetail));
        if (r && r.session) {
          // auto-signed-in (email confirmation off) → App routes them to billing;
          // a teammate has no plan to pick: the invite accept reloads them into the team
          if (!invited) { try { localStorage.setItem("fw_new_signup", "1"); } catch (e) {} }
          return;
        }
        setNote("Account created — you can sign in now.");
        setMode("signin");
      } else {
        await auth.signIn(email, pw);
      }
    } catch (e2) {
      setErr((e2 && (e2.message || e2.error_description)) || "Something went wrong");
    } finally { setBusy(false); }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-bg" />
      <form className="auth-card fade-up" onSubmit={submit}>
        <div className="auth-brand">
          <LogoMarkOnce size={36} />
          <span className="display" style={{ fontSize: 22 }}>Fence<span style={{ color: "var(--teal)" }}>Works</span></span>
        </div>
        {(fromSite || (claiming && mode !== "forgot")) && <span className="kicker kicker-teal" style={{ marginTop: 16 }}>{claiming ? "Your new website is ready" : "Step 4 of 4 · Almost there"}</span>}
        <h1 className="display" style={{ fontSize: 24, margin: fromSite || (claiming && mode !== "forgot") ? "10px 0 4px" : "18px 0 4px" }}>
          {invited ? <><span style={{ color: "var(--teal)" }}>{invite.business}</span> invited you</> : mode === "signin" ? "Sign in to your account" : mode === "forgot" ? "Reset your password" : fromSite ? "Set your password" : claiming ? "Claim your website" : "Create your account"}
        </h1>
        <p className="muted" style={{ fontSize: 13.5, margin: "0 0 22px" }}>
          {invited
            ? (mode === "signup"
                ? <>Join their team on FenceWorks as <b style={{ color: "var(--fg-2)" }}>{invite.role === "admin" ? "an admin" : "a team member"}</b> — set your name and a password. Your email is locked to the invite.</>
                : "Sign in with this email and you'll join their team automatically.")
            : claiming && mode === "signup" ? "Create your account and the website we built for you opens in your Website editor — review it, then publish it on your own domain when you're happy."
            : claiming && mode === "signin" ? "Sign in and we'll add the website we built for you to your account."
            : mode === "signin" ? "Welcome back. Your estimates, quotes, and crews are synced to the cloud."
            : mode === "forgot" ? "Enter your account email and we'll send you a secure reset link."
            : fromSite ? <>You picked the <b style={{ color: "var(--fg-2)" }}>{planName}</b> plan{pendingPlan !== "free" ? " — your 14-day free trial starts after checkout and nothing is charged until it ends" : ""}. Your name and company carried over; set a password and you're in.</>
            : "Start estimating, quoting, and collecting payments in one place."}
        </p>
        {invite === "bad" && (
          <div className="pe-callout info" style={{ marginBottom: 14 }}>
            <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>That team invite link is no longer valid — ask for a fresh one. You can still sign in or create your own account below.</p>
          </div>
        )}

        {mode === "signup" && (
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Full name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sam Carter" autoComplete="name" />
          </div>
        )}
        {mode === "signup" && !invited && (
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Company name</label>
            <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Evergreen Fence Co." autoComplete="organization" />
          </div>
        )}
        {/* Attribution. Asked here too, not just on the marketing site: this is
            the one path that ALWAYS creates an account (direct visitors, and
            anyone who skipped the signup wizard). Hidden for invited teammates
            — the inviter is the source, and asking them is noise. Skipped when
            the marketing site already asked, so nobody answers it twice. */}
        {mode === "signup" && !invited && !heardPrefilled && (
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">How did you hear about us? <span className="faint" style={{ fontWeight: 400 }}>(optional)</span></label>
            <select className="select" value={heard} onChange={(e) => { setHeard(e.target.value); if (!HEARD_DETAIL.has(e.target.value)) setHeardDetail(""); }}>
              <option value="">Choose one…</option>
              {HEARD_ABOUT.map(([v, lb]) => <option key={v} value={v}>{lb}</option>)}
            </select>
            {HEARD_DETAIL.has(heard) && (
              <input className="input" style={{ marginTop: 8 }} maxLength={160} value={heardDetail} onChange={(e) => setHeardDetail(e.target.value)}
                placeholder={heard === "referral" ? "Who referred you? (so we can thank them)"
                  : heard === "facebook_group" ? "Which group?"
                  : "Tell us where"} />
            )}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label className="field-label">Email{invited ? <span className="faint" style={{ fontWeight: 400 }}> — locked to the invite</span> : null}</label>
          <input className="input" type="email" required value={email} disabled={!!invited} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" style={invited ? { opacity: .7, cursor: "not-allowed" } : undefined} />
        </div>
        {mode !== "forgot" && (
          <div style={{ marginBottom: 16 }}>
            <div className="row between">
              <label className="field-label">Password</label>
              {mode === "signin" && <button type="button" onClick={() => { setMode("forgot"); setErr(""); setNote(""); }} style={{ background: "none", border: 0, color: "var(--teal)", fontSize: 12, cursor: "pointer", padding: 0 }}>Forgot password?</button>}
            </div>
            <input className="input" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" autoComplete={mode === "signin" ? "current-password" : "new-password"} />
          </div>
        )}

        {err && <div className="auth-msg err">{err}</div>}
        {note && <div className="auth-msg ok">{note}</div>}

        <button className="btn btn-primary btn-block" type="submit" disabled={busy} style={{ marginTop: 4 }}>
          {busy ? "Working…" : mode === "signin" ? "Sign in" : mode === "forgot" ? "Email me a reset link" : "Create account"}
        </button>

        <div className="auth-switch">
          {mode === "signin"
            ? <>New to Fence Works? <button type="button" onClick={() => { setMode("signup"); setErr(""); }}>Create an account</button></>
            : mode === "forgot"
            ? <>Remembered it? <button type="button" onClick={() => { setMode("signin"); setErr(""); setNote(""); }}>Back to sign in</button></>
            : <>Already have an account? <button type="button" onClick={() => { setMode("signin"); setErr(""); }}>Sign in</button></>}
        </div>

      </form>
    </div>
  );
}

/* Shown once after a password-recovery link signs the user in (flag is set
   pre-boot in app.tsx) — sets the new password on the recovery session. */
function RecoveryGate({ auth }: any) {
  const [show, setShow] = React.useState<any>(() => { try { return localStorage.getItem("fw_pw_recovery") === "1"; } catch (e) { return false; } });
  const [pw, setPw] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  if (!show) return null;
  const done = () => { try { localStorage.removeItem("fw_pw_recovery"); } catch (e) {} setShow(false); };
  async function save(e: any) {
    e.preventDefault();
    if ((pw || "").length < 8) { setErr("Use at least 8 characters."); return; }
    setBusy(true); setErr("");
    try { await auth.updatePassword(pw); done(); }
    catch (e2: any) { setErr(String((e2 && e2.message) || e2)); }
    finally { setBusy(false); }
  }
  return (
    <Portal>
    <div className="modal-scrim">
      <form className="modal" style={{ width: "min(420px, 96vw)" }} onSubmit={save}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 6px" }}>Set a new password</h3>
        <p className="muted" style={{ fontSize: 13, margin: "0 0 14px" }}>The reset link signed you in — choose a new password for next time.</p>
        <label className="field-label">New password</label>
        <input className="input" type="password" autoFocus value={pw} onChange={(e: any) => setPw(e.target.value)} autoComplete="new-password" placeholder="••••••••" />
        {err && <p style={{ color: "var(--amber)", fontSize: 12, margin: "8px 0 0" }}>{err}</p>}
        <div className="row gap-8" style={{ marginTop: 14 }}>
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>{busy ? "Saving…" : "Save password"}</button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={done}>Skip for now</button>
        </div>
      </form>
    </div>
    </Portal>
  );
}

Object.assign(window, { useAuth, AuthScreen, RecoveryGate });

export { useAuth, AuthScreen, RecoveryGate };
